/**
 * A compact ICU MessageFormat implementation (§10).
 *
 * "No string concatenation anywhere" is the actual requirement, and it is the
 * one that survives contact with Hindi, Tamil and Arabic. Supported:
 *
 *   {name}
 *   {count, number}                      {amount, number, percent}
 *   {at, date, short}                    {at, time, short}
 *   {n, plural, =0 {…} one {…} other {…}}   with # substitution
 *   {who, select, host {…} other {…}}
 *
 * Plural categories come from Intl.PluralRules, so Hindi's two-form system and
 * Arabic's six-form system are handled by the platform rather than by a table
 * someone has to maintain.
 */

export type MessageValues = Record<string, string | number | Date | undefined>;

type SimpleNode = {
  kind: 'simple';
  arg: string;
  format?: 'number' | 'date' | 'time';
  style?: string;
};
type ChoiceNode = {
  kind: 'plural' | 'select' | 'selectordinal';
  arg: string;
  offset: number;
  options: Record<string, Node[]>;
};
type Node = { kind: 'text'; value: string } | SimpleNode | ChoiceNode;

const cache = new Map<string, Node[]>();

export function parseMessage(message: string): Node[] {
  const cached = cache.get(message);
  if (cached) return cached;
  const parsed = parseNodes(message, { i: 0 }, false);
  cache.set(message, parsed);
  return parsed;
}

interface Cursor {
  i: number;
}

function parseNodes(src: string, cur: Cursor, nested: boolean): Node[] {
  const nodes: Node[] = [];
  let text = '';
  while (cur.i < src.length) {
    const ch = src[cur.i]!;
    if (ch === '}' && nested) break;
    if (ch === "'" && src[cur.i + 1] === '{') {
      // ICU apostrophe escaping: '{' is a literal brace.
      const end = src.indexOf("'", cur.i + 1);
      text += end === -1 ? src.slice(cur.i + 1) : src.slice(cur.i + 1, end);
      cur.i = end === -1 ? src.length : end + 1;
      continue;
    }
    if (ch === '{') {
      if (text) {
        nodes.push({ kind: 'text', value: text });
        text = '';
      }
      cur.i++;
      nodes.push(parseArgument(src, cur));
      continue;
    }
    text += ch;
    cur.i++;
  }
  if (text) nodes.push({ kind: 'text', value: text });
  return nodes;
}

function parseArgument(src: string, cur: Cursor): Node {
  const arg = readUntil(src, cur, [',', '}']).trim();
  if (src[cur.i] === '}') {
    cur.i++;
    return { kind: 'simple', arg };
  }
  cur.i++; // comma
  const type = readUntil(src, cur, [',', '}']).trim();

  if (type === 'number' || type === 'date' || type === 'time') {
    let style: string | undefined;
    if (src[cur.i] === ',') {
      cur.i++;
      style = readUntil(src, cur, ['}']).trim();
    }
    cur.i++; // closing brace
    return { kind: 'simple', arg, format: type, ...(style ? { style } : {}) };
  }

  if (type !== 'plural' && type !== 'select' && type !== 'selectordinal') {
    // Unknown type: treat as a plain substitution rather than throwing at a
    // user in production.
    readUntil(src, cur, ['}']);
    cur.i++;
    return { kind: 'simple', arg };
  }

  cur.i++; // comma after the type
  const options: Record<string, Node[]> = {};
  let offset = 0;
  while (cur.i < src.length && src[cur.i] !== '}') {
    skipSpace(src, cur);
    if (src[cur.i] === '}') break;
    const key = readUntil(src, cur, ['{', '}']).trim();
    if (key.startsWith('offset:')) {
      offset = Number.parseInt(key.slice(7), 10) || 0;
      continue;
    }
    if (src[cur.i] !== '{') break;
    cur.i++;
    options[key] = parseNodes(src, cur, true);
    cur.i++; // closing brace of the option
    skipSpace(src, cur);
  }
  cur.i++; // closing brace of the argument
  return { kind: type, arg, offset, options };
}

function readUntil(src: string, cur: Cursor, stops: string[]): string {
  let out = '';
  while (cur.i < src.length && !stops.includes(src[cur.i]!)) {
    out += src[cur.i];
    cur.i++;
  }
  return out;
}

function skipSpace(src: string, cur: Cursor): void {
  while (cur.i < src.length && /\s/.test(src[cur.i]!)) cur.i++;
}

const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
const pluralRules = new Map<string, Intl.PluralRules>();

function numberFormat(locale: string, style?: string): Intl.NumberFormat {
  const key = `${locale}|${style ?? ''}`;
  let fmt = numberFormats.get(key);
  if (!fmt) {
    const options: Intl.NumberFormatOptions =
      style === 'percent'
        ? { style: 'percent' }
        : style === 'integer'
          ? { maximumFractionDigits: 0 }
          : {};
    fmt = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, fmt);
  }
  return fmt;
}

function dateFormat(locale: string, kind: 'date' | 'time', style?: string): Intl.DateTimeFormat {
  const key = `${locale}|${kind}|${style ?? ''}`;
  let fmt = dateFormats.get(key);
  if (!fmt) {
    const s = (style ?? 'short') as 'short' | 'medium' | 'long' | 'full';
    fmt = new Intl.DateTimeFormat(
      locale,
      kind === 'date' ? { dateStyle: s } : { timeStyle: s },
    );
    dateFormats.set(key, fmt);
  }
  return fmt;
}

function plural(locale: string, type: 'cardinal' | 'ordinal'): Intl.PluralRules {
  const key = `${locale}|${type}`;
  let rules = pluralRules.get(key);
  if (!rules) {
    rules = new Intl.PluralRules(locale, { type });
    pluralRules.set(key, rules);
  }
  return rules;
}

export function formatMessage(
  message: string,
  values: MessageValues,
  locale: string,
): string {
  return render(parseMessage(message), values, locale, null);
}

function render(
  nodes: Node[],
  values: MessageValues,
  locale: string,
  pluralValue: number | null,
): string {
  let out = '';
  for (const node of nodes) {
    if (node.kind === 'text') {
      out += pluralValue === null ? node.value : node.value.replaceAll('#', String(pluralValue));
      continue;
    }
    if (node.kind === 'simple') {
      const value = values[node.arg];
      if (value === undefined) continue;
      if (node.format === 'number') {
        out += numberFormat(locale, node.style).format(Number(value));
      } else if (node.format === 'date' || node.format === 'time') {
        out += dateFormat(locale, node.format, node.style).format(new Date(value as never));
      } else {
        out += String(value);
      }
      continue;
    }

    const raw = values[node.arg];
    if (node.kind === 'select') {
      const branch = node.options[String(raw)] ?? node.options['other'] ?? [];
      out += render(branch, values, locale, pluralValue);
      continue;
    }
    const n = Number(raw ?? 0);
    const adjusted = n - node.offset;
    const exact = node.options[`=${n}`];
    if (exact) {
      out += render(exact, values, locale, adjusted);
      continue;
    }
    const category = plural(
      locale,
      node.kind === 'selectordinal' ? 'ordinal' : 'cardinal',
    ).select(adjusted);
    const branch = node.options[category] ?? node.options['other'] ?? [];
    out += render(branch, values, locale, adjusted);
  }
  return out;
}

/**
 * The argument names a message actually uses.
 *
 * Walks the parsed message rather than pattern-matching braces: in
 * `{guard, select, on {on} other {off}}` the inner braces are branch *bodies*,
 * and a regex reads them as two more arguments. That mistake makes a
 * catalogue-parity check fail on correct translations and pass on broken ones.
 */
export function messageArguments(message: string): string[] {
  const names = new Set<string>();
  const walk = (nodes: Node[]): void => {
    for (const node of nodes) {
      if (node.kind === 'text') continue;
      names.add(node.arg);
      if (node.kind !== 'simple') {
        for (const branch of Object.values(node.options)) walk(branch);
      }
    }
  };
  walk(parseMessage(message));
  return [...names].sort();
}
